import type {
  ProposalSavingsAiComputeDto,
  ProposalSavingsDto,
} from "~/api/dto/proposal.dto"

/** The compute node's guaranteed base, as V1's savings count it. */
export type ProposalSavingsComputeBase = NonNullable<
  ProposalSavingsAiComputeDto[`base`]
>

/**
 * The guaranteed base V1's savings include (Hosting Agreement Rev 2.10), or
 * none without a node the gateway can pay out. The network bonus is never
 * counted.
 */
export const shownProposalSavingsCompute = (
  savings: Pick<ProposalSavingsDto, `aiCompute`> | null | undefined,
): ProposalSavingsComputeBase | undefined => {
  const base = savings?.aiCompute?.base
  return base && base.totalIncome > 0 ? base : undefined
}

/**
 * The savings years the base is counted in: every savings year within the
 * hosting term.
 */
export const proposalSavingsComputeYears = (
  savings: Pick<ProposalSavingsDto, `aiCompute`> | null | undefined,
): number => {
  const aiCompute = savings?.aiCompute
  if (!aiCompute) return 0
  // An older gateway's payload can carry `aiCompute` without `payout`.
  const termYears = aiCompute.payout?.termYears ?? aiCompute.incomeYears
  return Math.max(Math.min(aiCompute.incomeYears, termYears), 0)
}

/** Net savings as V1 shows them: with the node's base when there is one. */
export const shownNetSavings = (
  savings:
    | Pick<ProposalSavingsDto, `aiCompute` | `netSavings`>
    | null
    | undefined,
): number =>
  shownProposalSavingsCompute(savings)?.netSavingsWithCompute ??
  savings?.netSavings ??
  0

/**
 * A cumulative per-year series with the base added from year one, for the
 * savings years the gateway counts it in.
 */
export const withCumulativeComputeIncome = <Point extends { y: number }>(
  points: readonly Point[],
  savings: Pick<ProposalSavingsDto, `aiCompute`> | null | undefined,
): Point[] => {
  const base = shownProposalSavingsCompute(savings)
  if (!base) return [...points]
  const incomeYears = proposalSavingsComputeYears(savings)

  return points.map((point, index) => ({
    ...point,
    y: point.y + base.yearlyIncome * Math.min(index + 1, incomeYears),
  }))
}
